import { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function StudentWorkSlider() {
  const [works, setWorks] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'student_works'), orderBy('createdAt', 'desc'), limit(10));
    const unsub = onSnapshot(q, (snap) => {
      setWorks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  // Auto-play every 6 seconds
  const next = useCallback(() => {
    if (works.length === 0) return;
    setActiveIndex(prev => (prev + 1) % works.length);
  }, [works.length]);

  useEffect(() => {
    if (works.length <= 1) return;
    const interval = setInterval(next, 6000);
    return () => clearInterval(interval);
  }, [next, works.length, activeIndex]);

  if (loading || works.length === 0) return null;

  // Calculate card positions relative to activeIndex
  const getCardStyle = (index) => {
    const total = works.length;
    let offset = (index - activeIndex + total) % total;

    if (offset === 0) {
      return { zIndex: 10, scale: 1, y: 0, x: 0, opacity: 1, rotateZ: 0, brightness: 1 };
    } else if (offset === 1) {
      return { zIndex: 8, scale: 0.92, y: -20, x: 15, opacity: 0.85, rotateZ: 2, brightness: 0.7 };
    } else if (offset === 2) {
      return { zIndex: 6, scale: 0.84, y: -38, x: 28, opacity: 0.6, rotateZ: 4, brightness: 0.5 };
    } else if (offset === total - 1) {
      // the card going to the back
      return { zIndex: 4, scale: 0.76, y: -54, x: 40, opacity: 0.3, rotateZ: 6, brightness: 0.3 };
    } else {
      return { zIndex: 1, scale: 0.7, y: 0, x: 0, opacity: 0, rotateZ: 0, brightness: 0 };
    }
  };

  const activeWork = works[activeIndex];

  return (
    <section className="relative py-24 md:py-32 overflow-hidden" dir="rtl">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-500/10 dark:bg-primary-500/10 border border-primary-500/20 rounded-full text-primary-600 dark:text-primary-400 text-sm font-bold mb-4">
            <Sparkles className="w-4 h-4" />
            إبداعات طلابنا
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white mb-4">
            أعمال مبدعي لباب
          </h2>
          <p className="text-lg text-slate-500 dark:text-zinc-400 font-medium max-w-xl mx-auto">
            شاهد ما حققه طلابنا المتميزون بعد الالتحاق بدوراتنا الاحترافية
          </p>
        </div>

        {/* Split Layout */}
        <div className="flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-20">

          {/* Right Side: Typography (RTL so this appears on the right) */}
          <div className="flex-1 text-center lg:text-right space-y-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeWork?.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="space-y-4"
              >
                <h3 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-tight">
                  {activeWork?.studentName}
                </h3>
                <p className="text-xl font-bold text-primary-600 dark:text-primary-400">
                  دورة : {activeWork?.courseName}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Dots indicator */}
            <div className="flex items-center gap-2 justify-center lg:justify-start mt-8">
              {works.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`rounded-full transition-all duration-300 ${i === activeIndex
                    ? 'w-8 h-3 bg-primary-500'
                    : 'w-3 h-3 bg-slate-300 dark:bg-zinc-700 hover:bg-primary-300 dark:hover:bg-primary-800'
                    }`}
                  aria-label={`عرض عمل ${i + 1}`}
                />
              ))}
            </div>

            {/* CTA */}
            <Link
              to="/creators"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-2xl transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary-500/25 mt-4"
            >
              <Sparkles className="w-5 h-5" />
              عرض جميع الأعمال
            </Link>
          </div>

          {/* Left Side: Stacked Cards (RTL so this appears on the left) */}
          <div className="flex-1 w-full flex items-center justify-center">
            <div className="relative w-[320px] h-[200px] sm:w-[540px] sm:h-[320px]">
              {works.map((work, index) => {
                const style = getCardStyle(index);
                if (style.opacity === 0) return null;

                return (
                  <motion.div
                    key={work.id}
                    className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl cursor-pointer border-2 border-white/20"
                    animate={{
                      scale: style.scale,
                      y: style.y,
                      x: style.x,
                      zIndex: style.zIndex,
                      opacity: style.opacity,
                      rotate: style.rotateZ,
                    }}
                    transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
                    onClick={() => setActiveIndex(index)}
                    style={{ filter: `brightness(${style.brightness})` }}
                  >
                    <img
                      src={work.imageUrl}
                      alt={work.studentName}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Story Style Timeline (Progress Bar) */}
                    {style.zIndex === 10 && (
                      <div className="absolute bottom-0 inset-x-0 p-4 z-50">
                        <div className="h-1.5 w-full bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
                          <motion.div
                            key={activeIndex}
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: 6, ease: "linear" }}
                            className="h-full bg-white origin-left"
                          />
                        </div>
                      </div>
                    )}

                    {/* Bottom label on front card */}
                    {style.zIndex === 10 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="absolute bottom-0 inset-x-0 p-12 bg-gradient-to-t from-primary-500/80 via-primary-500/40 to-transparent dark:from-primary-500/90 dark:via-primary-500/50 dark:to-transparent"
                      >
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
